import React, { useCallback, useEffect, useState } from 'react';
import {
  Mail,
  Search,
  Loader2,
  RefreshCw,
  Eye,
  CheckCircle2,
  XCircle,
  Inbox,
  Database,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import api from '@/lib/api';
import { toast } from 'sonner';

const statusBadge = {
  sent: 'bg-emerald-100 text-emerald-800',
  failed: 'bg-red-100 text-red-800',
  error: 'bg-red-100 text-red-800',
};

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return String(iso);
  }
};

const OutboundEmailsTab = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [qDraft, setQDraft] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  // Varsayılan: sipariş / işlem mailleri (eski kampanya logları ayrı sekmede)
  const [view, setView] = useState('transactional');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/outbound-emails', {
        params: {
          page,
          limit: 30,
          view,
          q: q || undefined,
          status: status === 'all' ? undefined : status,
          category: category === 'all' ? undefined : category,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setCategories(data.categories || []);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Giden mailler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [page, q, status, category, view]);

  const runBackfill = useCallback(async ({ silent } = {}) => {
    setBackfilling(true);
    try {
      const { data } = await api.post('/admin/outbound-emails/backfill-orders');
      const c = data?.cancellation || 0;
      const o = data?.confirmation || 0;
      if (!silent || c > 0 || o > 0) {
        toast.success(`Arşive eklendi: ${o} onay, ${c} iptal maili`);
      }
      setView('transactional');
      setPage(1);
      setLoading(true);
      const res = await api.get('/admin/outbound-emails', {
        params: { page: 1, limit: 30, view: 'transactional' },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
      setCategories(res.data.categories || []);
    } catch (e) {
      if (!silent) toast.error(e.response?.data?.detail || 'Geri yükleme başarısız');
    } finally {
      setBackfilling(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // İlk açılışta sipariş mailleri boşsa otomatik arşive yükle
  useEffect(() => {
    if (loading || backfilling) return;
    if (view !== 'transactional') return;
    if (total > 0) return;
    const key = 'outbound_emails_autobackfill_v1';
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    runBackfill({ silent: true });
  }, [loading, backfilling, view, total, runBackfill]);

  const openDetail = async (id) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/admin/outbound-emails/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Detay yüklenemedi');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="outbound-emails-tab">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Inbox className="w-5 h-5" />
                Giden mailler
              </CardTitle>
              <CardDescription className="mt-1">
                Sipariş onay/iptal ve diğer işlem mailleri burada. Eski kampanya logları ayrı sekmede.
                Eksik sipariş mailleri için «Siparişlerden yükle» kullanın.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => runBackfill()}
                disabled={backfilling || loading}
                data-testid="outbound-email-backfill"
              >
                {backfilling ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                ) : (
                  <Database className="w-3.5 h-3.5 mr-1.5" />
                )}
                Siparişlerden yükle
              </Button>
              <Button variant="outline" size="sm" onClick={load} disabled={loading}>
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
                Yenile
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'transactional', label: 'Sipariş & işlem' },
              { id: 'legacy', label: 'Eski kampanya logları' },
              { id: 'all', label: 'Tümü' },
            ].map((tab) => (
              <Button
                key={tab.id}
                size="sm"
                variant={view === tab.id ? 'default' : 'outline'}
                className="h-8"
                onClick={() => {
                  setPage(1);
                  setView(tab.id);
                }}
              >
                {tab.label}
              </Button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
              <Input
                className="pl-8 h-9"
                placeholder="E-posta, konu, sipariş no..."
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setPage(1);
                    setQ(qDraft.trim());
                  }
                }}
                data-testid="outbound-email-search"
              />
            </div>
            <Button
              size="sm"
              className="h-9"
              onClick={() => {
                setPage(1);
                setQ(qDraft.trim());
              }}
            >
              Ara
            </Button>
            <Select
              value={status}
              onValueChange={(v) => {
                setPage(1);
                setStatus(v);
              }}
            >
              <SelectTrigger className="w-[140px] h-9">
                <SelectValue placeholder="Durum" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm durumlar</SelectItem>
                <SelectItem value="sent">Gönderildi</SelectItem>
                <SelectItem value="failed">Başarısız</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={category}
              onValueChange={(v) => {
                setPage(1);
                setCategory(v);
              }}
            >
              <SelectTrigger className="w-[200px] h-9">
                <SelectValue placeholder="Kategori" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm kategoriler</SelectItem>
                <SelectItem value="order_confirmation">Sipariş onayı</SelectItem>
                <SelectItem value="order_cancellation">Sipariş iptali</SelectItem>
                {categories
                  .filter((c) => !['order_confirmation', 'order_cancellation'].includes(c))
                  .map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-gray-500">{total} kayıt · görünüm: {view}</p>

          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-sm space-y-3">
              <Mail className="w-8 h-8 mx-auto opacity-40" />
              <p>
                {view === 'transactional'
                  ? 'Henüz sipariş/işlem maili yok. «Siparişlerden yükle» ile geçmiş onay ve iptal maillerini arşive ekleyin.'
                  : 'Bu görünümde kayıt yok.'}
              </p>
              {view === 'transactional' && (
                <Button size="sm" onClick={() => runBackfill()} disabled={backfilling}>
                  {backfilling ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  Siparişlerden yükle
                </Button>
              )}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Tarih</TableHead>
                    <TableHead className="text-xs">Alıcı</TableHead>
                    <TableHead className="text-xs">Konu</TableHead>
                    <TableHead className="text-xs">Kategori</TableHead>
                    <TableHead className="text-xs">Durum</TableHead>
                    <TableHead className="text-xs text-right">İşlem</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-xs text-gray-600 whitespace-nowrap">
                        {formatWhen(row.sent_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="font-medium truncate max-w-[180px]" title={row.to_email}>
                          {row.to_email || '—'}
                        </div>
                        {row.to_name && (
                          <div className="text-[11px] text-gray-500 truncate">{row.to_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm max-w-[280px]">
                        <span className="line-clamp-2" title={row.subject}>
                          {row.subject || '—'}
                        </span>
                        {row.meta?.order_number && (
                          <span className="text-[10px] text-gray-500 block mt-0.5">
                            Sipariş #{row.meta.order_number}
                          </span>
                        )}
                        {!row.has_body && (
                          <span className="text-[10px] text-amber-700 block mt-0.5">
                            Özet kayıt (gövde yok)
                          </span>
                        )}
                        {row.provider === 'backfill' && (
                          <span className="text-[10px] text-blue-700 block mt-0.5">
                            Siparişten arşivlendi
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] font-normal">
                          {row.category === 'order_cancellation'
                            ? 'Sipariş iptali'
                            : row.category === 'order_confirmation'
                              ? 'Sipariş onayı'
                              : row.category || '—'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            statusBadge[row.status] || 'bg-gray-100'
                          }`}
                        >
                          {row.status === 'sent' ? (
                            <CheckCircle2 className="w-3 h-3" />
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          {row.status === 'sent' ? 'Gönderildi' : row.status || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8"
                          onClick={() => openDetail(row.id)}
                        >
                          <Eye className="w-3.5 h-3.5 mr-1" />
                          Görüntüle
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between pt-1">
              <Button
                size="sm"
                variant="outline"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Önceki
              </Button>
              <span className="text-xs text-gray-500">
                Sayfa {page} / {pages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={page >= pages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Sonraki
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Giden mail detayı</DialogTitle>
            <DialogDescription>
              {detail ? formatWhen(detail.sent_at) : 'Yükleniyor...'}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : detail ? (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Alıcı:</span>{' '}
                  <span className="font-medium">{detail.to_email}</span>
                  {detail.to_name ? ` (${detail.to_name})` : ''}
                </div>
                <div>
                  <span className="text-gray-500">Gönderen:</span>{' '}
                  <span className="font-medium">
                    {detail.from_name || ''} {detail.from_email ? `<${detail.from_email}>` : '—'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Kategori:</span> {detail.category || '—'}
                </div>
                <div>
                  <span className="text-gray-500">Sağlayıcı:</span> {detail.provider || '—'}
                </div>
                <div>
                  <span className="text-gray-500">Durum:</span> {detail.status || '—'}
                </div>
                {detail.meta?.order_number && (
                  <div>
                    <span className="text-gray-500">Sipariş:</span> #{detail.meta.order_number}
                  </div>
                )}
                {detail.meta?.backfill && (
                  <div className="sm:col-span-2 text-blue-700">
                    Bu kayıt siparişten arşive eklendi (yeniden gönderilmedi); içerik şablondan
                    yeniden oluşturuldu.
                  </div>
                )}
                {detail.legacy_source && (
                  <div>
                    <span className="text-gray-500">Eski kaynak:</span> {detail.legacy_source}
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">Konu</div>
                <div className="font-medium border rounded-md px-3 py-2 bg-gray-50">
                  {detail.subject || '—'}
                </div>
              </div>
              {detail.error && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-2">
                  Hata: {detail.error}
                </div>
              )}
              <div>
                <div className="text-xs text-gray-500 mb-1">İçerik</div>
                {detail.has_body && detail.html_body ? (
                  <div className="border rounded-md overflow-hidden bg-white">
                    <iframe
                      title="email-preview"
                      sandbox=""
                      className="w-full min-h-[420px] bg-white"
                      srcDoc={detail.html_body}
                    />
                  </div>
                ) : (
                  <div className="border rounded-md px-3 py-4 text-xs text-gray-600 bg-amber-50 border-amber-200">
                    {detail.body_preview ||
                      'Bu kayıt eski logdan aktarıldı; HTML gövde saklanmamış.'}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OutboundEmailsTab;
